var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import clsx from 'clsx';
import { get } from 'lodash';
import React, { Fragment, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import Box from '@material-ui/core/Box';
import Collapse from '@material-ui/core/Collapse';
import Paper from '@material-ui/core/Paper';
import { createStyles, lighten, makeStyles } from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TablePagination from '@material-ui/core/TablePagination';
import TableRow from '@material-ui/core/TableRow';
import Toolbar from '@material-ui/core/Toolbar';
import Typography from '@material-ui/core/Typography';
import ProcessSearchContext from '../../../../context/process-search-context';
import { extractValueFromProcessObject } from '../../../../helpers/response';
import { paginationSearchQuery } from '../../../../helpers/search';
import { useProcessList } from '../../../../hooks';
import { searchProcessData_AC } from '../../../../store/actions/process';
import { processDataCountSelector } from '../../../../store/selectors/process';
import StyledIcon from '../../../CustomStyledIcon';
import FilterMenu from '../../../FilterMenu';
import SelectedFilters from '../../../SelectedFilters';
function EmbeddedRelatedProcessTableHead(props) {
    var columns = props.columns, appliedFilters = props.appliedFilters, onApplyFilter = props.onApplyFilter;
    var t = useTranslation().t;
    return (React.createElement(TableHead, null,
        React.createElement(TableRow, null,
            React.createElement(TableCell, { padding: "checkbox" }),
            columns.map(function (column, index) { return (React.createElement(TableCell, { key: column.alias, align: column.align || 'left', padding: index === 0 ? 'none' : 'default' },
                t("" + column.label),
                !column.noFilter && (React.createElement(FilterMenu, { column: column, onApplyFilter: onApplyFilter, appliedFilters: appliedFilters })))); }))));
}
var useToolbarStyles = makeStyles(function (theme) {
    return createStyles({
        root: {
            paddingLeft: theme.spacing(2),
            paddingRight: theme.spacing(1),
        },
        highlight: theme.palette.type === 'light'
            ? {
                color: theme.palette.secondary.main,
                backgroundColor: lighten(theme.palette.secondary.light, 0.85),
            }
            : {
                color: theme.palette.text.primary,
                backgroundColor: theme.palette.secondary.dark,
            },
        title: {
            flex: '1 1 100%',
        },
        filtersToolBar: {
            backgroundColor: '#efefef',
            border: '1px dotted grey',
        },
    });
});
var EmbeddedRelatedProcessTableToolbar = function (props) {
    var _a;
    var classes = useToolbarStyles({});
    var title = props.title, numSelected = props.numSelected, filters = props.filters, onDeleteFilter = props.onDeleteFilter;
    return (React.createElement(Fragment, null,
        React.createElement(Toolbar, { className: clsx(classes.root, (_a = {},
                _a[classes.highlight] = numSelected > 0,
                _a)) }, numSelected > 0 ? (React.createElement(Typography, { className: classes.title, color: "inherit", variant: "subtitle1", component: "div" },
            numSelected,
            " selected")) : (React.createElement(Typography, { className: classes.title, variant: "h6", id: "tableTitle", component: "div" }, title))),
        Object.keys(filters).length > 0 && (React.createElement(Toolbar, { className: classes.filtersToolBar },
            React.createElement(Typography, null, "Filters: "),
            React.createElement(SelectedFilters, { filters: filters, handleDeleteFilter: onDeleteFilter })))));
};
var useStyles = makeStyles(function (theme) {
    return createStyles({
        root: {
            width: '100%',
        },
        paper: {
            width: '100%',
            marginBottom: theme.spacing(2),
        },
        table: {
            minWidth: 750,
        },
        tableContainer: {
            overflow: 'unset',
        },
        visuallyHidden: {
            border: 0,
            clip: 'rect(0 0 0 0)',
            height: 1,
            margin: -1,
            overflow: 'hidden',
            padding: 0,
            position: 'absolute',
            top: 20,
            width: 1,
        },
    });
});
var EmbeddedRelatedProcessTable = function (_a) {
    var _b = _a.value, value = _b === void 0 ? {} : _b, title = _a.title, modelAlias = _a.modelAlias, tableDef = _a.tableDef, maxInstances = _a.maxInstances, onSelect = _a.onSelect, onRemove = _a.onRemove, expandedRow = _a.expandedRow, _c = _a.defaultQuery, defaultQuery = _c === void 0 ? [] : _c;
    var _d = tableDef.columns, columns = _d === void 0 ? [] : _d;
    var _e = useState(-1), activeRow = _e[0], setActiveRow = _e[1];
    var classes = useStyles({});
    var selected = value;
    var _f = useState(0), page = _f[0], setPage = _f[1];
    var _g = useState(5), rowsPerPage = _g[0], setRowsPerPage = _g[1];
    var rows = useProcessList(modelAlias);
    var psContext = useContext(ProcessSearchContext);
    var totalRows = useSelector(processDataCountSelector(modelAlias, psContext.name));
    var _h = useState({}), filters = _h[0], setFilters = _h[1];
    var dispatch = useDispatch();
    var disableSelection = maxInstances === -1
        ? false
        : get(Object.keys(selected), 'length', 0) >= maxInstances;
    var filterQuery = useMemo(function () {
        return Object.keys(filters).map(function (key) {
            var selector = key;
            var selectorValue = filters[key];
            var comparison = 'contains';
            if (typeof selectorValue === 'string') {
                return {
                    selector: selector,
                    comparison: comparison,
                    value: selectorValue,
                };
            }
            if (typeof selectorValue === 'boolean') {
                comparison = '=';
                if (selectorValue === false) {
                    comparison = 'not';
                }
                return {
                    selector: selector,
                    comparison: comparison,
                    value: "" + selectorValue,
                };
            }
            if (typeof selectorValue === 'object') {
                return {
                    selector: selector,
                    value: selectorValue.id,
                };
            }
            return [];
        });
    }, [filters]);
    useEffect(function () {
        dispatch(searchProcessData_AC(modelAlias, paginationSearchQuery(modelAlias, filterQuery, rowsPerPage, page + 1, defaultQuery), psContext.name));
    }, [dispatch, modelAlias, psContext.name, filterQuery, rowsPerPage, page]);
    var onApplyFilter = function (filter) {
        setFilters(function (filters) {
            return __assign(__assign({}, filters), filter);
        });
    };
    var handleDeleteFilter = function (filter) {
        setFilters(function (filters) {
            delete filters[filter];
            return __assign({}, filters);
        });
    };
    var handleChangePage = function (event, newPage) {
        setPage(newPage);
    };
    var handleChangeRowsPerPage = function (event) {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };
    var isSelected = function (id) {
        return !!selected[id];
    };
    return (React.createElement("div", { className: classes.root },
        React.createElement(Paper, { className: classes.paper },
            React.createElement(EmbeddedRelatedProcessTableToolbar, { title: title, numSelected: Object.keys(selected).length, filters: filters, onDeleteFilter: handleDeleteFilter }),
            React.createElement(TableContainer, { className: classes.tableContainer },
                React.createElement(Table, { className: classes.table, "aria-labelledby": "tableTitle", size: 'small', "aria-label": "enhanced table" },
                    React.createElement(EmbeddedRelatedProcessTableHead, { classes: classes, rowCount: rows.length, columns: columns, onApplyFilter: onApplyFilter, appliedFilters: filters, disableSelection: disableSelection }),
                    React.createElement(TableBody, null, rows.map(function (row, index) {
                        var isItemSelected = isSelected(row.id);
                        var labelId = "enhanced-table-checkbox-" + index;
                        var open = activeRow === index;
                        return (React.createElement(Fragment, null,
                            React.createElement(TableRow, { hover: true, role: "checkbox", "aria-checked": isItemSelected, tabIndex: -1, key: row.id, selected: isItemSelected },
                                React.createElement(TableCell, { padding: "checkbox", onClick: function () { return setActiveRow(open ? -1 : index); } }, open ? (React.createElement(StyledIcon, null, "keyboard_arrow_down")) : (React.createElement(StyledIcon, null, "keyboard_arrow_right"))),
                                columns.map(function (column, colIndex) {
                                    var cellValue = extractValueFromProcessObject(row, column.alias);
                                    return (React.createElement(TableCell, { key: "row-" + index + "-column-" + colIndex, id: labelId, padding: colIndex === 0 ? 'none' : 'default', align: column.align }, column.customRender
                                        ? column.customRender(cellValue, row)
                                        : cellValue));
                                })),
                            React.createElement(TableRow, null,
                                React.createElement(TableCell, { style: { paddingBottom: 0, paddingTop: 0 }, colSpan: 9 },
                                    React.createElement(Collapse, { in: open, timeout: "auto", unmountOnExit: true },
                                        React.createElement(Box, { marginTop: 2, marginBottom: 4 }, expandedRow({ row: row, onSelect: onSelect, onRemove: onRemove, value: value })))))));
                    })))),
            React.createElement(TablePagination, { rowsPerPageOptions: [5, 10, 15], component: "div", count: totalRows, rowsPerPage: rowsPerPage, page: page, onChangePage: handleChangePage, onChangeRowsPerPage: handleChangeRowsPerPage }))));
};
export default EmbeddedRelatedProcessTable;
//# sourceMappingURL=index.js.map